import { HttpClient, HttpEvent } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { first, Observable } from 'rxjs';
import { CommonResponse } from '../models';
import { University } from '../models/university/university';
import { ProfileUserUniversity } from '../models/profile/profile';
import { SchoolUniversity } from '../models/school/school-university';
import { CurrentUniversityService } from './current-university.service';
import { filter } from 'rxjs/operators';
import { AuthenticationService } from './authentication.service';
import { File, UniversityFileTypes } from '../models/file';

@Injectable({
	providedIn: 'root',
})
export class UniversityService {
	constructor(
		private _http: HttpClient,
		private _currentUniversityService: CurrentUniversityService,
		private _authenticationService: AuthenticationService
	) {
		if (this._currentUniversityService.getUniversityFromStorage() && this._authenticationService.activeProfileId.value) {
			this._currentUniversityService.currentUniversityId
				.pipe(
					filter(currenUniversityId => !!currenUniversityId),
					first()
				)
				.subscribe(currenUniversityId => {
					if (currenUniversityId !== null) {
						this.showUniversity(currenUniversityId).subscribe(res => {
							this._currentUniversityService.currentUniversitySubject.next(res.data);
						});
					}
				});
		}
	}

	index(): Observable<CommonResponse<Array<University>>> {
		return this._http.get<CommonResponse<Array<University>>>(`/universities`);
	}

	getProfileUsers(id: string): Observable<CommonResponse<Array<ProfileUserUniversity>>> {
		return this._http.get<CommonResponse<Array<ProfileUserUniversity>>>(`/universities/${id}/profile_users`);
	}

	getSchoolUniversities(id: string): Observable<CommonResponse<Array<SchoolUniversity>>> {
		return this._http.get<CommonResponse<Array<SchoolUniversity>>>(`/universities/${id}/school_universities`);
	}

	insertUniversity(university: FormData): Observable<HttpEvent<CommonResponse<University>>> {
		return this._http.post<CommonResponse<University>>(`/universities`, university, {
			reportProgress: true,
			observe: 'events',
		});
	}

	showUniversity(id: string): Observable<CommonResponse<University>> {
		return this._http.get<CommonResponse<University>>(`/universities/${id}`);
	}

	editUniversity(id: string, university: FormData): Observable<HttpEvent<CommonResponse<University>>> {
		university.append('_method', 'PUT');
		return this._http.post<CommonResponse<University>>(`/universities/${id}`, university, {
			reportProgress: true,
			observe: 'events',
		});
	}

	showUniversityWithDomain(): Observable<CommonResponse<University>> {
		return this._http.get<CommonResponse<University>>(`/universities/searched_by/domain_name`);
	}

	uploadFile(id: string, document: FormData): Observable<HttpEvent<CommonResponse<boolean>>> {
		return this._http.post<CommonResponse<boolean>>(`/universities/${id}/upload_file`, document, {
			reportProgress: true,
			observe: 'events',
		});
	}

	downloadFile(
		id: string,
		relation: {
			relation: UniversityFileTypes;
		}
	): Observable<CommonResponse<File>> {
		return this._http.post<CommonResponse<File>>(`/universities/${id}/download_file`, relation);
	}

	deleteFile(
		id: string,
		relation: {
			relation: UniversityFileTypes;
		}
	): Observable<CommonResponse<boolean>> {
		return this._http.delete<CommonResponse<boolean>>(`/universities/${id}/destroy_file`, {
			params: { relation: relation.relation },
		});
	}

	uploadFileForUniversity(document: FormData): Observable<HttpEvent<CommonResponse<boolean>>> {
		return this._http.post<CommonResponse<boolean>>(`/university/upload_file`, document, {
			reportProgress: true,
			observe: 'events',
		});
	}

	downloadFileForUniversity(relation: 'signature' | 'stamp'): Observable<CommonResponse<File>> {
		return this._http.post<CommonResponse<File>>(`/university/download_file`, { relation });
	}

	deleteFileForUniversity(relation: 'signature' | 'stamp'): Observable<CommonResponse<boolean>> {
		return this._http.delete<CommonResponse<boolean>>(`/university/destroy_file`, {
			params: { relation },
		});
	}
}
